import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Wrench, Flame, Zap, Home, Trees, Waves, Fence, Hammer, TreePine, Bug, Car, HelpCircle,
  MapPin, Users, Clock, HandHeart, ArrowRight, Check, Sparkles,
  Globe, Star, Search, Facebook, Instagram, Loader2, Phone, Building2, AlertCircle,
} from "lucide-react";
import AppShell from "@/components/AppShell";
import WelcomeExperience from "@/components/WelcomeExperience";
import { toast } from "sonner";
import { discoverBusiness, discoverBusinessFromUrl, confirmBusiness, saveSocialPreferences, saveHelpAreas, saveBriefPreference } from "@/lib/api";

const TRADES = [
  { id: "plumbing", label: "Plumbing", icon: Wrench },
  { id: "hvac", label: "HVAC", icon: Flame },
  { id: "electrical", label: "Electrical", icon: Zap },
  { id: "roofing", label: "Roofing", icon: Home },
  { id: "landscaping", label: "Landscaping", icon: Trees },
  { id: "pool", label: "Pool", icon: Waves },
  { id: "fence", label: "Fence", icon: Fence },
  { id: "remodeling", label: "Remodeling", icon: Hammer },
  { id: "tree", label: "Tree Service", icon: TreePine },
  { id: "pest", label: "Pest Control", icon: Bug },
  { id: "automotive_services", label: "Automotive Services", icon: Car },
  { id: "other_service", label: "Other Service Business", icon: HelpCircle },
];

const PRIORITIES = [
  { id: "customers", label: "I need more customers", icon: Users },
  { id: "time", label: "I don't have enough time", icon: Clock },
  { id: "handled", label: "I just want things handled", icon: HandHeart },
];

const TOTAL_STEPS = 7;

export default function Onboarding() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("welcome"); // welcome | step | done
  const [step, setStep] = useState(1);
  const [trade, setTrade] = useState(null);
  const [customBusinessType, setCustomBusinessType] = useState("");
  const [zip, setZip] = useState("");
  const [city, setCity] = useState("");
  const [priority, setPriority] = useState(null);
  const [confirmedBusiness, setConfirmedBusiness] = useState(null); // full payload from /confirm-business
  const [socialPrefs, setSocialPrefs] = useState({
    usesFacebook: false,
    facebookPageUrl: "",
    wantsFacebookPostingHelp: false,
    usesInstagram: false,
    instagramUsername: "",
    wantsInstagramPostingHelp: false,
  });
  const [helpAreas, setHelpAreas] = useState([]);
  const [helpAreaOther, setHelpAreaOther] = useState("");
  const [briefPrefs, setBriefPrefs] = useState({ dayOfWeek: 0, hour: 7 });
  const [stepSaving, setStepSaving] = useState(false);

  // Read draft (email + businessName) from SignUp.
  const signupDraft = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("ta-signup-draft") || "{}");
    } catch (_) { return {}; }
  }, []);

  const canAdvance = () => {
    if (step === 1) {
      if (!trade) return false;
      // Fallback category requires a customer-entered description.
      if (trade === "other_service" && !customBusinessType.trim()) return false;
      return true;
    }
    if (step === 2) return zip.length >= 3 && city.trim().length >= 2;
    if (step === 3) return !!priority;
    if (step === 4) return !!confirmedBusiness;
    if (step === 5) {
      // Social step is optional; if "Other" is not on this step we always
      // allow continue. Owner may skip both platforms entirely.
      return true;
    }
    if (step === 6) {
      // Help-areas step is optional as a whole, but if "Other" is checked
      // the owner must describe it before finishing setup.
      if (helpAreas.includes("other") && !helpAreaOther.trim()) return false;
      return true;
    }
    if (step === 7) {
      // Delivery time is a simple pair of picks. We default to Mon 7am so
      // the owner can always advance without changing anything.
      return true;
    }
    return false;
  };

  const next = async () => {
    if (stepSaving) return;
    // Persist owner-declared data as they advance. Failures are non-fatal —
    // the owner can still reach the dashboard; we just toast the error.
    if (step === 5) {
      setStepSaving(true);
      try {
        await saveSocialPreferences({
          usesFacebook: !!socialPrefs.usesFacebook,
          facebookPageUrl: socialPrefs.facebookPageUrl.trim() || null,
          wantsFacebookPostingHelp: !!socialPrefs.wantsFacebookPostingHelp,
          usesInstagram: !!socialPrefs.usesInstagram,
          instagramUsername: socialPrefs.instagramUsername.trim() || null,
          wantsInstagramPostingHelp: !!socialPrefs.wantsInstagramPostingHelp,
        });
      } catch (e) {
        toast.error("We couldn't save that just now. You can update it in Settings later.");
      } finally {
        setStepSaving(false);
      }
    }
    if (step === 6) {
      setStepSaving(true);
      try {
        await saveHelpAreas({
          helpAreas,
          helpAreaOther: helpAreas.includes("other") ? helpAreaOther.trim() : null,
        });
      } catch (e) {
        toast.error("We couldn't save that just now. You can update it in Settings later.");
      } finally {
        setStepSaving(false);
      }
    }
    if (step === 7) {
      setStepSaving(true);
      try {
        await saveBriefPreference({
          dayOfWeek: briefPrefs.dayOfWeek,
          hour: briefPrefs.hour,
          minute: 0,
        });
      } catch (e) {
        toast.error("We couldn't save that just now. You can update it in Settings later.");
      } finally {
        setStepSaving(false);
      }
    }
    if (step < TOTAL_STEPS) setStep(step + 1);
    else setPhase("done");
  };
  const back = () => {
    if (step > 1) setStep(step - 1);
    else setPhase("welcome");
  };

  if (phase === "welcome") return <Welcome onStart={() => setPhase("step")} />;
  if (phase === "done") {
    // Send owners straight to the Post-Onboarding Results page — they get
    // an immediate look at what we already learned about their business
    // and their AI Visibility Snapshot, instead of waiting until Monday.
    const bizId = confirmedBusiness?.id || confirmedBusiness?.businessId;
    const goTo = bizId
      ? () => navigate(`/onboarding/results/${bizId}`)
      : () => navigate("/dashboard");
    return <Completion onGo={goTo} />;
  }

  return (
    <AppShell
      showBack
      testId="onboarding-page"
      showTopBar
    >
      <div className="pt-2 sm:pt-4">
        {/* Progress */}
        <ProgressBar step={step} total={TOTAL_STEPS} />

        {/* Steps */}
        <div key={step} className="page-fade">
          {step === 1 && <StepTrade trade={trade} setTrade={setTrade} customBusinessType={customBusinessType} setCustomBusinessType={setCustomBusinessType} />}
          {step === 2 && <StepZip zip={zip} setZip={setZip} city={city} setCity={setCity} />}
          {step === 3 && <StepPriority priority={priority} setPriority={setPriority} />}
          {step === 4 && (
            <StepDiscovery
              signupDraft={signupDraft}
              trade={trade}
              customBusinessType={customBusinessType}
              zip={zip}
              city={city}
              confirmedBusiness={confirmedBusiness}
              setConfirmedBusiness={setConfirmedBusiness}
            />
          )}
          {step === 5 && (
            <StepSocial socialPrefs={socialPrefs} setSocialPrefs={setSocialPrefs} />
          )}
          {step === 6 && (
            <StepHelpAreas
              helpAreas={helpAreas}
              setHelpAreas={setHelpAreas}
              helpAreaOther={helpAreaOther}
              setHelpAreaOther={setHelpAreaOther}
            />
          )}
          {step === 7 && (
            <StepBriefTime briefPrefs={briefPrefs} setBriefPrefs={setBriefPrefs} />
          )}
        </div>

        {/* Advance */}
        <div className="mt-10 flex flex-col gap-3">
          <button
            data-testid="onboarding-next-button"
            onClick={next}
            disabled={!canAdvance()}
            className="w-full inline-flex items-center justify-center gap-2 min-h-[64px] rounded-2xl text-lg font-medium text-white transition-transform hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0"
            style={{ background: "var(--ta-primary)" }}
          >
            {step < TOTAL_STEPS ? "Continue" : "Finish setup"} <ArrowRight size={20} />
          </button>

          {step > 1 && (
            <button
              onClick={back}
              data-testid="onboarding-back-inline"
              className="text-sm font-medium py-2"
              style={{ color: "var(--ta-text-secondary)" }}
            >
              Go back
            </button>
          )}
        </div>
      </div>
    </AppShell>
  );
}

/* ---------- Progress ---------- */
const ProgressBar = ({ step, total }) => (
  <div className="mb-8" data-testid="onboarding-progress">
    <div className="flex items-center justify-between mb-2">
      <span
        className="text-xs uppercase tracking-widest font-semibold"
        style={{ color: "var(--ta-secondary)" }}
        data-testid="onboarding-progress-label"
      >
        Step {step} of {total}
      </span>
      <span className="text-xs" style={{ color: "var(--ta-text-secondary)" }}>
        About {Math.max(1, total - step + 1) * 20}s left
      </span>
    </div>
    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--ta-surface-alt)" }}>
      <div
        className="h-full rounded-full transition-[width] duration-500 ease-out"
        style={{
          width: `${(step / total) * 100}%`,
          background: "var(--ta-primary)",
        }}
      />
    </div>
  </div>
);

/* ---------- Welcome ---------- */
const Welcome = ({ onStart }) => (
  <AppShell showBack backTo="/signup" testId="onboarding-welcome">
    <div className="pt-6 sm:pt-10 max-w-lg page-fade">
      <div
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium mb-6"
        style={{ background: "var(--ta-accent)", color: "var(--ta-primary)" }}
      >
        <Sparkles size={14} strokeWidth={2.2} />
        About 2 minutes. That's it.
      </div>

      <h1
        className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]"
        style={{ color: "var(--ta-primary)" }}
        data-testid="welcome-headline"
      >
        Let's get your first Monday Brief ready.
      </h1>

      <p className="mt-4 text-lg leading-relaxed" style={{ color: "var(--ta-text-secondary)" }}>
        A few quick questions and we'll take it from there. No forms to wrestle with. No busywork.
      </p>

      <ul className="mt-8 space-y-4">
        {[
          "We only ask what helps us write you a better brief.",
          "Wherever we can, we do the work for you — no typing.",
          "You'll pick your delivery day and time at the end.",
        ].map((line, i) => (
          <li key={i} className="flex items-start gap-3">
            <span
              className="inline-flex items-center justify-center w-7 h-7 rounded-full mt-0.5 shrink-0"
              style={{ background: "var(--ta-success-bg)", color: "var(--ta-success)" }}
            >
              <Check size={16} strokeWidth={2.6} />
            </span>
            <span className="text-base leading-relaxed" style={{ color: "var(--ta-text)" }}>
              {line}
            </span>
          </li>
        ))}
      </ul>

      <button
        data-testid="welcome-start-button"
        onClick={onStart}
        className="mt-10 w-full inline-flex items-center justify-center gap-2 min-h-[64px] rounded-2xl text-lg font-medium text-white transition-transform hover:-translate-y-0.5"
        style={{ background: "var(--ta-primary)" }}
      >
        Let's begin <ArrowRight size={20} />
      </button>
    </div>
  </AppShell>
);

/* ---------- Step 1: Trade ---------- */
const StepTrade = ({ trade, setTrade, customBusinessType, setCustomBusinessType }) => (
  <div data-testid="onboarding-step-1">
    <StepTitle
      eyebrow="First, the easy one"
      title="What type of business do you run?"
      sub="This helps us compare you with the right local businesses and tailor your Monday Brief."
    />
    <div className="mt-6 grid grid-cols-2 gap-3">
      {TRADES.map((t) => {
        const active = trade === t.id;
        return (
          <button
            key={t.id}
            data-testid={`trade-option-${t.id}`}
            onClick={() => setTrade(t.id)}
            className="text-left rounded-2xl p-5 min-h-[96px] border-2 transition-all active:scale-[0.98]"
            style={{
              background: active ? "var(--ta-primary)" : "#fff",
              borderColor: active ? "var(--ta-primary)" : "var(--ta-border)",
              color: active ? "#fff" : "var(--ta-primary)",
            }}
          >
            <t.icon size={22} strokeWidth={2.2} />
            <div className="mt-3 font-display font-semibold text-base">{t.label}</div>
          </button>
        );
      })}
    </div>

    {trade === "other_service" && (
      <div className="mt-6" data-testid="onboarding-custom-business-type-container">
        <label
          className="block text-sm font-semibold mb-2"
          style={{ color: "var(--ta-primary)" }}
        >
          What kind of business is it? <span style={{ color: "var(--ta-accent, #D96C51)" }}>*</span>
        </label>
        <input
          type="text"
          value={customBusinessType}
          onChange={(e) => setCustomBusinessType(e.target.value)}
          placeholder="e.g. Mobile Notary, Window Tinting, Small Engine Repair"
          data-testid="onboarding-custom-business-type-input"
          maxLength={80}
          className="w-full px-4 py-3 rounded-2xl border-2 outline-none text-base bg-white"
          style={{ borderColor: "var(--ta-border)", color: "var(--ta-primary)" }}
        />
        <div className="mt-2 text-xs" style={{ color: "var(--ta-text-secondary)" }}>
          A short description helps us find your Google listing accurately.
        </div>
      </div>
    )}
  </div>
);

/* ---------- Step 2: City + ZIP ---------- */
const StepZip = ({ zip, setZip, city, setCity }) => (
  <div data-testid="onboarding-step-2">
    <StepTitle
      eyebrow="Where you work"
      title="Which city and ZIP do you serve?"
      sub="This helps us see your service area, compare you with the shops down the street, and tell AI models where to find you."
    />
    <div className="mt-8 space-y-4">
      <label className="block">
        <div className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "var(--ta-text-secondary)" }}>
          City
        </div>
        <div
          className="flex items-center gap-3 px-5 rounded-2xl bg-white border-2 focus-within:border-[var(--ta-primary)]"
          style={{ borderColor: "var(--ta-border)", minHeight: 72 }}
        >
          <MapPin size={22} style={{ color: "var(--ta-primary)" }} />
          <input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            data-testid="onboarding-city-input"
            placeholder="Palm Bay"
            className="w-full text-xl font-display font-semibold bg-transparent outline-none"
            style={{ color: "var(--ta-primary)" }}
          />
        </div>
      </label>
      <label className="block">
        <div className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "var(--ta-text-secondary)" }}>
          ZIP code
        </div>
        <div
          className="flex items-center gap-3 px-5 rounded-2xl bg-white border-2 focus-within:border-[var(--ta-primary)]"
          style={{ borderColor: "var(--ta-border)", minHeight: 72 }}
        >
          <MapPin size={22} style={{ color: "var(--ta-primary)" }} />
          <input
            inputMode="numeric"
            maxLength={5}
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
            data-testid="onboarding-zip-input"
            placeholder="83702"
            className="w-full text-xl font-display font-semibold bg-transparent outline-none tracking-widest"
            style={{ color: "var(--ta-primary)" }}
          />
        </div>
      </label>
      <p className="text-sm" style={{ color: "var(--ta-text-secondary)" }}>
        We only use this to check how you show up locally. Nothing else.
      </p>
    </div>
  </div>
);

/* ---------- Step 3: Priority ---------- */
const StepPriority = ({ priority, setPriority }) => (
  <div data-testid="onboarding-step-3">
    <StepTitle
      eyebrow="Between us"
      title="What's on your mind these days?"
      sub="Pick whichever feels most true. You can change it anytime."
    />
    <div className="mt-6 flex flex-col gap-3">
      {PRIORITIES.map((p) => {
        const active = priority === p.id;
        return (
          <button
            key={p.id}
            data-testid={`priority-option-${p.id}`}
            onClick={() => setPriority(p.id)}
            className="w-full text-left rounded-2xl p-5 border-2 flex items-center gap-4 active:scale-[0.99] transition-all"
            style={{
              background: active ? "var(--ta-primary)" : "#fff",
              borderColor: active ? "var(--ta-primary)" : "var(--ta-border)",
              color: active ? "#fff" : "var(--ta-primary)",
              minHeight: 72,
            }}
          >
            <div
              className="inline-flex items-center justify-center w-11 h-11 rounded-full shrink-0"
              style={{
                background: active ? "rgba(255,255,255,0.15)" : "var(--ta-accent)",
                color: active ? "#fff" : "var(--ta-primary)",
              }}
            >
              <p.icon size={20} strokeWidth={2.2} />
            </div>
            <span className="font-display font-medium text-lg leading-snug flex-1">
              {p.label}
            </span>
            {active && <Check size={22} className="shrink-0" />}
          </button>
        );
      })}
    </div>
  </div>
);

/* ---------- Step 4: Live Business Discovery ---------- */
const StepDiscovery = ({ signupDraft, trade, customBusinessType, zip, city, confirmedBusiness, setConfirmedBusiness }) => {
  const [status, setStatus] = useState("idle"); // idle | scanning | ready | manual | confirming | error
  const [candidates, setCandidates] = useState([]);
  const [message, setMessage] = useState("");
  const [errorCode, setErrorCode] = useState("");
  // When the URL fallback resolves a candidate, we swap the results
  // list and flip the outer state to `ready` so "That's mine" / "Search
  // again" / "None of these" still work as before.
  const onUrlResolved = (list) => {
    setCandidates(list);
    setMessage("");
    setErrorCode("");
    setStatus("ready");
  };
  const [manualForm, setManualForm] = useState({
    website: "",
    phone: "",
    city: "",
    state: "",
    serviceDescription: "",
  });
  const requestKey = useRef("");

  const businessName = (signupDraft?.businessName || "").trim();
  const email = (signupDraft?.email || "").trim();

  const search = async () => {
    if (!businessName) {
      setStatus("manual");
      setMessage("We need your business name to look you up. You can enter your details manually.");
      return;
    }
    const key = `${businessName}|${trade}|${zip}`;
    if (requestKey.current === key && candidates.length > 0) {
      setStatus("ready");
      return;
    }
    requestKey.current = key;
    setStatus("scanning");
    setMessage("");
    setErrorCode("");
    try {
      const data = await discoverBusiness({
        businessName,
        trade: trade || undefined,
        customBusinessType: customBusinessType || undefined,
        zipCode: zip || undefined,
        regionCode: "US",
      });
      if (!data.enabled) {
        setStatus("manual");
        setMessage(data.message || "Live business discovery is not configured yet.");
        return;
      }
      setCandidates(Array.isArray(data.candidates) ? data.candidates : []);
      setMessage(data.message || "");
      setErrorCode(data.errorCode || "");
      setStatus((Array.isArray(data.candidates) && data.candidates.length > 0) ? "ready" : "manual");
    } catch (e) {
      setStatus("manual");
      setMessage("We couldn't complete that search. You can enter your details manually.");
    }
  };

  useEffect(() => { search(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pickCandidate = async (candidate) => {
    setStatus("confirming");
    try {
      const result = await confirmBusiness({
        placeId: candidate.placeId,
        businessName,
        email,
        ownerFirstName: "",
        trade,
        customBusinessType: customBusinessType || null,
        // Owner-entered ZIP + City from Step 2. Used as a fallback location
        // when Google Places returns no address (pure service-area businesses)
        // AND passed through to the AI Visibility engine so LLM probes have
        // real location context.
        zipCode: zip || null,
        city: city || null,
        confidenceScore: candidate.score,
        confidenceLabel: candidate.confidence,
      });
      setConfirmedBusiness(result?.business || { placeId: candidate.placeId, name: candidate.name });
      toast.success("Great — we've got the right shop.");
    } catch (e) {
      setStatus("ready");
      toast.error("We couldn't confirm that one. Please try another.");
    }
  };

  const submitManual = async () => {
    const city = manualForm.city.trim();
    const state = manualForm.state.trim();
    if (!city || !state) {
      toast.error("Please add your city and state so we can find you.");
      return;
    }
    setStatus("confirming");
    try {
      const result = await confirmBusiness({
        manual: true,
        businessName,
        email,
        trade,
        customBusinessType: customBusinessType || null,
        serviceDescription: manualForm.serviceDescription.trim() || null,
        city,
        state,
        zipCode: zip,
        website: manualForm.website.trim() || null,
        phone: manualForm.phone.trim() || null,
      });
      setConfirmedBusiness(result?.business || { name: businessName, manual: true });
      toast.success("Perfect — we saved your details.");
    } catch (e) {
      setStatus("manual");
      toast.error("Something went wrong saving that. Please try again.");
    }
  };

  return (
    <div data-testid="onboarding-step-4">
      <StepTitle
        eyebrow="No typing needed"
        title={
          confirmedBusiness ? "You're all set." :
          status === "scanning" ? "Hold on — we're looking you up." :
          status === "manual" ? "Let's do it your way." :
          "Here's what we found."
        }
        sub={
          confirmedBusiness ? "Nice work. We'll take it from here."
          : status === "scanning" ? "Checking the usual places customers look for you…"
          : status === "manual" ? "No worries — a couple of quick fields and we're done."
          : "Pick the one that's yours. If none of these look right, you can enter it manually."
        }
      />

      {confirmedBusiness ? (
        <ConfirmedCard business={confirmedBusiness} />
      ) : status === "scanning" ? (
        <div className="mt-8 flex flex-col gap-3" data-testid="discovery-scanning">
          <ScanRow icon={Search} label={businessName ? `Looking for "${businessName}"…` : "Looking up local businesses…"} />
          <ScanRow icon={Star} label="Checking your Google Business Profile…" delay={0.15} />
        </div>
      ) : status === "manual" ? (
        <>
          <ManualEntryForm
            businessName={businessName}
            zip={zip}
            trade={trade}
            form={manualForm}
            setForm={setManualForm}
            message={message}
            onSubmit={submitManual}
            onRetrySearch={search}
            isSubmitting={status === "confirming"}
          />
          <UrlFallback onResolved={onUrlResolved} />
        </>
      ) : (
        <div className="mt-8 flex flex-col gap-4 stagger" data-testid="discovery-results">
          {candidates.map((c, idx) => (
            <CandidateCard
              key={c.placeId || idx}
              index={idx}
              candidate={c}
              onPick={() => pickCandidate(c)}
              disabled={status === "confirming"}
            />
          ))}
          <button
            data-testid="discovery-none-manual"
            onClick={() => setStatus("manual")}
            className="mt-2 inline-flex items-center justify-center gap-2 min-h-[52px] rounded-xl text-base font-medium border-2 transition-transform hover:-translate-y-0.5"
            style={{ borderColor: "var(--ta-border)", color: "var(--ta-primary)", background: "#fff" }}
          >
            None of these — enter manually
          </button>
          <button
            data-testid="discovery-search-again"
            onClick={search}
            className="text-sm font-medium py-2 inline-flex items-center justify-center gap-1"
            style={{ color: "var(--ta-text-secondary)" }}
          >
            <Search size={14} /> Search again
          </button>

          <UrlFallback onResolved={onUrlResolved} />
        </div>
      )}
    </div>
  );
};

const ScanRow = ({ icon: Icon, label, delay = 0 }) => (
  <div
    className="flex items-center gap-4 p-5 rounded-2xl bg-white border"
    style={{ borderColor: "var(--ta-border)", animationDelay: `${delay}s` }}
  >
    <div
      className="w-11 h-11 rounded-full inline-flex items-center justify-center shrink-0"
      style={{ background: "var(--ta-accent)", color: "var(--ta-primary)" }}
    >
      <Icon size={20} strokeWidth={2.2} />
    </div>
    <span className="flex-1 text-base" style={{ color: "var(--ta-text)" }}>{label}</span>
    <Loader2 size={20} className="animate-spin shrink-0" style={{ color: "var(--ta-primary)" }} />
  </div>
);

const CONFIDENCE_STYLES = {
  high:   { bg: "var(--ta-success-bg)", fg: "var(--ta-success)", label: "Strong match" },
  medium: { bg: "var(--ta-accent)",     fg: "var(--ta-primary)", label: "Likely match"  },
  low:    { bg: "#F5F1EA",              fg: "#7A6A56",           label: "Possible match" },
};

const CandidateCard = ({ candidate, onPick, disabled, index }) => {
  const conf = CONFIDENCE_STYLES[candidate.confidence] || CONFIDENCE_STYLES.low;
  return (
    <div
      className="p-5 rounded-2xl bg-white border-2 transition-colors"
      style={{ borderColor: "var(--ta-border)" }}
      data-testid={`discovery-candidate-${index}`}
    >
      <div className="flex items-start gap-4">
        <div
          className="w-11 h-11 rounded-xl inline-flex items-center justify-center shrink-0"
          style={{ background: "var(--ta-accent)", color: "var(--ta-primary)" }}
        >
          <Building2 size={20} strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="text-[11px] uppercase tracking-widest font-semibold inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full"
            style={{ background: conf.bg, color: conf.fg }}
            data-testid={`discovery-candidate-${index}-confidence`}
          >
            {conf.label}
          </div>
          <div className="font-display font-semibold text-lg mt-1.5 truncate" style={{ color: "var(--ta-primary)" }}>
            {candidate.name}
          </div>
          {candidate.address && (
            <div className="text-sm mt-0.5 flex items-start gap-1.5" style={{ color: "var(--ta-text-secondary)" }}>
              <MapPin size={14} className="mt-0.5 shrink-0" /> <span className="truncate">{candidate.address}</span>
            </div>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" style={{ color: "var(--ta-text-secondary)" }}>
            {typeof candidate.rating === "number" && (
              <span className="inline-flex items-center gap-1"><Star size={14} /> {candidate.rating.toFixed(1)}{candidate.reviewCount ? ` · ${candidate.reviewCount} reviews` : ""}</span>
            )}
            {candidate.phone && <span className="inline-flex items-center gap-1"><Phone size={14} /> {candidate.phone}</span>}
            {candidate.website && <span className="inline-flex items-center gap-1 truncate"><Globe size={14} /> <span className="truncate">{candidate.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}</span></span>}
          </div>
        </div>
      </div>
      <button
        onClick={onPick}
        disabled={disabled}
        data-testid={`discovery-candidate-${index}-pick`}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 min-h-[52px] rounded-xl text-base font-medium text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
        style={{ background: "var(--ta-primary)" }}
      >
        <Check size={18} /> That's mine
      </button>
    </div>
  );
};

// Small, self-contained fallback so an owner whose business isn't picked
// up by Text Search can paste a Google Business Profile / Google Maps
// link. Delegates to `POST /api/onboarding/discover/from-url` and hands
// resolved candidates back to the parent via `onResolved(list)` so the
// existing CandidateCard flow (That's mine / Search again / None of
// these) keeps working.
const UrlFallback = ({ onResolved }) => {
  const [value, setValue] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | error
  const [msg, setMsg] = useState("");

  const submit = async () => {
    const url = value.trim();
    if (!url) return;
    setState("loading");
    setMsg("");
    try {
      const data = await discoverBusinessFromUrl(url);
      const list = Array.isArray(data.candidates) ? data.candidates : [];
      if (list.length > 0) {
        setState("idle");
        setMsg("");
        setValue("");
        onResolved(list);
      } else {
        setState("error");
        setMsg(data.message || "We couldn't identify a Google Business Profile from that link. Try copying the business link directly from Google Maps, search again, or continue manually.");
      }
    } catch (_e) {
      setState("error");
      setMsg("We couldn't identify a Google Business Profile from that link. Try copying the business link directly from Google Maps, search again, or continue manually.");
    }
  };

  return (
    <div
      className="mt-8 pt-6 border-t"
      style={{ borderColor: "var(--ta-border)" }}
      data-testid="discovery-url-fallback"
    >
      <div className="text-sm font-medium mb-1" style={{ color: "var(--ta-primary)" }}>
        Can&apos;t find your business?
      </div>
      <p className="text-xs mb-3" style={{ color: "var(--ta-text-secondary)" }}>
        Paste your Google Business Profile or Google Maps link.
      </p>
      <div
        className="flex items-stretch gap-2 rounded-2xl bg-white border-2 px-3 py-2"
        style={{ borderColor: "var(--ta-border)" }}
      >
        <input
          type="url"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="https://maps.app.goo.gl/…"
          className="flex-1 bg-transparent outline-none text-sm"
          style={{ color: "var(--ta-text)" }}
          data-testid="discovery-url-input"
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
        />
        <button
          onClick={submit}
          disabled={state === "loading" || !value.trim()}
          data-testid="discovery-url-submit"
          className="text-sm font-semibold px-4 rounded-xl disabled:opacity-60"
          style={{ background: "var(--ta-accent, #D96C51)", color: "#fff" }}
        >
          {state === "loading" ? "Finding…" : "Find my business"}
        </button>
      </div>
      {state === "error" && msg && (
        <div className="mt-3 text-xs" style={{ color: "#7A2E1E" }} data-testid="discovery-url-error">
          {msg}
        </div>
      )}
    </div>
  );
};


const ManualEntryForm = ({ businessName, zip, trade, form, setForm, message, onSubmit, onRetrySearch, isSubmitting }) => (
  <div className="mt-6 flex flex-col gap-4" data-testid="discovery-manual">
    {message && (
      <div
        className="p-4 rounded-2xl border flex items-start gap-3"
        style={{ background: "var(--ta-accent)", borderColor: "var(--ta-border)" }}
      >
        <AlertCircle size={18} className="mt-0.5 shrink-0" style={{ color: "var(--ta-primary)" }} />
        <p className="text-sm" style={{ color: "var(--ta-text)" }}>{message}</p>
      </div>
    )}
    <ManualField
      icon={Building2}
      label="Business name"
      value={businessName || "—"}
      hint="From your sign-up. You can change it later in Settings."
      readOnly
      testId="manual-business-name"
    />
    {trade === "automotive_services" && (
      <ManualField
        icon={Car}
        label="What type of automotive service do you provide?"
        value={form.serviceDescription}
        onChange={(v) => setForm({ ...form, serviceDescription: v })}
        placeholder="Mobile Oil Change"
        hint="Be specific — this helps customers find you."
        testId="manual-service-description"
      />
    )}
    <ManualField
      icon={MapPin}
      label="City"
      value={form.city}
      onChange={(v) => setForm({ ...form, city: v })}
      placeholder="Palm Bay"
      testId="manual-city"
      required
    />
    <ManualField
      icon={MapPin}
      label="State"
      value={form.state}
      onChange={(v) => setForm({ ...form, state: v })}
      placeholder="FL"
      testId="manual-state"
      required
    />
    <ManualField
      icon={MapPin}
      label="ZIP code"
      value={zip || "—"}
      hint="From step 2."
      readOnly
      testId="manual-zip"
    />
    <ManualField
      icon={Globe}
      label="Website (optional)"
      value={form.website}
      onChange={(v) => setForm({ ...form, website: v })}
      placeholder="yourbusiness.com"
      testId="manual-website"
    />
    <ManualField
      icon={Phone}
      label="Business phone (optional)"
      value={form.phone}
      onChange={(v) => setForm({ ...form, phone: v })}
      placeholder="(555) 555-1234"
      testId="manual-phone"
    />
    <button
      data-testid="discovery-manual-save"
      onClick={onSubmit}
      disabled={isSubmitting || !form.city.trim() || !form.state.trim()}
      className="mt-2 inline-flex items-center justify-center gap-2 min-h-[56px] rounded-2xl text-base font-medium text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
      style={{ background: "var(--ta-primary)" }}
    >
      {isSubmitting ? <><Loader2 size={18} className="animate-spin" /> Saving…</> : <><Check size={18} /> Save my details</>}
    </button>
    <button
      onClick={onRetrySearch}
      data-testid="discovery-manual-retry-search"
      className="text-sm font-medium py-2 inline-flex items-center justify-center gap-1"
      style={{ color: "var(--ta-text-secondary)" }}
    >
      <Search size={14} /> Try searching again
    </button>
  </div>
);

const ManualField = ({ icon: Icon, label, value, onChange, placeholder, hint, readOnly, testId }) => (
  <label className="block">
    <span className="text-sm font-medium mb-2 inline-block" style={{ color: "var(--ta-primary)" }}>
      {label}
    </span>
    <div
      className="flex items-center gap-3 px-4 rounded-2xl bg-white border-2"
      style={{ borderColor: "var(--ta-border)", minHeight: 64, opacity: readOnly ? 0.85 : 1 }}
    >      <Icon size={18} style={{ color: "var(--ta-primary)" }} />
      <input
        readOnly={readOnly}
        value={value}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full h-full text-base bg-transparent outline-none py-3"
        style={{ color: "var(--ta-text)" }}
      />
    </div>
    {hint && (
      <p className="text-xs mt-1.5" style={{ color: "var(--ta-text-secondary)" }}>{hint}</p>
    )}
  </label>
);

const ConfirmedCard = ({ business }) => {
  const name = business?.businessName || business?.name || "Your business";
  const address = business?.discoveryDetails?.address || [business?.city, business?.state, business?.zipCode].filter(Boolean).join(", ");
  const website = business?.websiteUrl || business?.discoveryDetails?.website;
  const isManual = business?.discoverySource === "manual" || business?.manual;
  return (
    <div
      className="mt-8 p-5 rounded-2xl border-2"
      style={{ background: "var(--ta-success-bg)", borderColor: "var(--ta-success)" }}
      data-testid="discovery-confirmed"
    >
      <div className="flex items-start gap-4">
        <div
          className="w-11 h-11 rounded-xl inline-flex items-center justify-center shrink-0"
          style={{ background: "var(--ta-success)", color: "#fff" }}
        >
          <Check size={22} strokeWidth={2.6} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--ta-success)" }}>
            {isManual ? "Details saved" : "Confirmed"}
          </div>
          <div className="font-display font-semibold text-lg mt-0.5 truncate" style={{ color: "var(--ta-primary)" }}>
            {name}
          </div>
          {address && (
            <div className="text-sm mt-0.5" style={{ color: "var(--ta-text-secondary)" }}>{address}</div>
          )}
          {website && (
            <div className="text-sm mt-1 inline-flex items-center gap-1.5" style={{ color: "var(--ta-text-secondary)" }}>
              <Globe size={14} /> {website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------- Step 5: Your Social Media (owner-declared, NOT OAuth) ---------- */
const StepSocial = ({ socialPrefs, setSocialPrefs }) => {
  const patch = (key, value) => setSocialPrefs({ ...socialPrefs, [key]: value });
  return (
    <div data-testid="onboarding-step-5">
      <StepTitle
        eyebrow="Step 5 of 7"
        title="Your social media."
        sub="Tell us where your business is active and whether you would like help keeping your social pages updated. You can skip this step."
      />
      <div className="mt-6 flex flex-col gap-5">
        <SocialPlatformCard
          testIdPrefix="social-facebook"
          icon={Facebook}
          brand="#1877F2"
          platform="Facebook"
          usesKey="usesFacebook"
          urlKey="facebookPageUrl"
          urlLabel="Facebook page URL"
          urlPlaceholder="facebook.com/yourbusiness"
          helpKey="wantsFacebookPostingHelp"
          socialPrefs={socialPrefs}
          patch={patch}
        />
        <SocialPlatformCard
          testIdPrefix="social-instagram"
          icon={Instagram}
          brand="#E1306C"
          platform="Instagram"
          usesKey="usesInstagram"
          urlKey="instagramUsername"
          urlLabel="Instagram username"
          urlPlaceholder="@yourbusiness"
          helpKey="wantsInstagramPostingHelp"
          socialPrefs={socialPrefs}
          patch={patch}
        />
      </div>
      <p className="mt-6 text-sm" style={{ color: "var(--ta-text-secondary)" }}>
        We only save what you tell us here — we haven't connected to Facebook or Instagram, and we won't claim to be monitoring your accounts.
      </p>
    </div>
  );
};

const SocialPlatformCard = ({
  testIdPrefix,
  icon: Icon,
  brand,
  platform,
  usesKey,
  urlKey,
  urlLabel,
  urlPlaceholder,
  helpKey,
  socialPrefs,
  patch,
}) => {
  const uses = !!socialPrefs[usesKey];
  return (
    <div
      className="rounded-2xl border-2 bg-white p-5"
      style={{ borderColor: "var(--ta-border)" }}
      data-testid={`${testIdPrefix}-card`}
    >
      <div className="flex items-center gap-4">
        <div
          className="w-11 h-11 rounded-xl inline-flex items-center justify-center shrink-0"
          style={{ background: brand, color: "#fff" }}
        >
          <Icon size={22} strokeWidth={2.2} />
        </div>
        <div className="font-display font-semibold text-lg flex-1" style={{ color: "var(--ta-primary)" }}>
          {platform}
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-3">
        <OwnerCheckbox
          testId={`${testIdPrefix}-uses`}
          label={`I use ${platform}`}
          checked={uses}
          onChange={(v) => patch(usesKey, v)}
        />
        <OwnerCheckbox
          testId={`${testIdPrefix}-wants-help`}
          label="I would like help with posting"
          checked={!!socialPrefs[helpKey]}
          onChange={(v) => patch(helpKey, v)}
        />

        {uses && (
          <div className="pt-1">
            <label
              className="block text-sm font-medium mb-1.5"
              style={{ color: "var(--ta-primary)" }}
              htmlFor={`${testIdPrefix}-url`}
            >
              {urlLabel} <span className="text-xs" style={{ color: "var(--ta-text-secondary)" }}>(optional)</span>
            </label>
            <input
              id={`${testIdPrefix}-url`}
              data-testid={`${testIdPrefix}-url-input`}
              type="text"
              value={socialPrefs[urlKey]}
              onChange={(e) => patch(urlKey, e.target.value)}
              placeholder={urlPlaceholder}
              maxLength={300}
              className="w-full px-4 py-3 rounded-2xl border-2 outline-none text-base bg-white"
              style={{ borderColor: "var(--ta-border)", color: "var(--ta-primary)" }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const OwnerCheckbox = ({ testId, label, checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    data-testid={testId}
    role="checkbox"
    aria-checked={checked}
    className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl border-2 text-left transition-colors"
    style={{
      background: checked ? "var(--ta-success-bg)" : "#fff",
      borderColor: checked ? "var(--ta-success)" : "var(--ta-border)",
      minHeight: 56,
    }}
  >
    <span
      className="w-5 h-5 rounded-md inline-flex items-center justify-center shrink-0 border-2"
      style={{
        background: checked ? "var(--ta-success)" : "#fff",
        borderColor: checked ? "var(--ta-success)" : "var(--ta-border)",
        color: "#fff",
      }}
    >
      {checked && <Check size={14} strokeWidth={3} />}
    </span>
    <span className="text-base" style={{ color: "var(--ta-primary)" }}>{label}</span>
  </button>
);

/* ---------- Step 6: Where would you like the most help right now? ---------- */
const HELP_AREAS = [
  { id: "website", label: "My website" },
  { id: "google_business_profile", label: "My Google Business Profile" },
  { id: "customer_reviews", label: "Getting more customer reviews" },
  { id: "social_media", label: "Keeping social media active" },
  { id: "missed_calls", label: "Following up on missed calls" },
  { id: "other", label: "Other" },
  { id: "nothing_specific", label: "Nothing specific right now" },
];

const StepHelpAreas = ({ helpAreas, setHelpAreas, helpAreaOther, setHelpAreaOther }) => {
  const toggle = (id) => {
    // "Nothing specific" is exclusive — selecting it clears others, and
    // selecting anything else clears it. Keeps the intent honest.
    if (id === "nothing_specific") {
      setHelpAreas(helpAreas.includes(id) ? [] : ["nothing_specific"]);
      return;
    }
    if (helpAreas.includes(id)) {
      setHelpAreas(helpAreas.filter((x) => x !== id));
    } else {
      setHelpAreas([...helpAreas.filter((x) => x !== "nothing_specific"), id]);
    }
  };

  return (
    <div data-testid="onboarding-step-6">
      <StepTitle
        eyebrow="Step 6 of 7"
        title="Where would you like the most help right now?"
        sub="Select all that apply. This helps us understand what matters most to you. You are not signing up for any additional services."
      />
      <div className="mt-6 flex flex-col gap-3">
        {HELP_AREAS.map((area) => (
          <OwnerCheckbox
            key={area.id}
            testId={`help-area-${area.id}`}
            label={area.label}
            checked={helpAreas.includes(area.id)}
            onChange={() => toggle(area.id)}
          />
        ))}
      </div>

      {helpAreas.includes("other") && (
        <div className="mt-5" data-testid="help-area-other-container">
          <label
            className="block text-sm font-semibold mb-2"
            style={{ color: "var(--ta-primary)" }}
            htmlFor="help-area-other-input"
          >
            Tell us what you would like help with. <span style={{ color: "var(--ta-accent, #D96C51)" }}>*</span>
          </label>
          <input
            id="help-area-other-input"
            data-testid="help-area-other-input"
            type="text"
            value={helpAreaOther}
            onChange={(e) => setHelpAreaOther(e.target.value)}
            maxLength={300}
            placeholder="A short sentence is plenty."
            className="w-full px-4 py-3 rounded-2xl border-2 outline-none text-base bg-white"
            style={{ borderColor: "var(--ta-border)", color: "var(--ta-primary)" }}
          />
        </div>
      )}
    </div>
  );
};

/* ---------- Completion ---------- */
const DAYS_OF_WEEK = [
  { value: 0, short: "Mon", long: "Monday" },
  { value: 1, short: "Tue", long: "Tuesday" },
  { value: 2, short: "Wed", long: "Wednesday" },
  { value: 3, short: "Thu", long: "Thursday" },
  { value: 4, short: "Fri", long: "Friday" },
  { value: 5, short: "Sat", long: "Saturday" },
  { value: 6, short: "Sun", long: "Sunday" },
];

const HOUR_CHOICES = [
  { value: 6, label: "6 AM" },
  { value: 7, label: "7 AM" },
  { value: 8, label: "8 AM" },
  { value: 9, label: "9 AM" },
  { value: 10, label: "10 AM" },
  { value: 12, label: "Noon" },
];

const StepBriefTime = ({ briefPrefs, setBriefPrefs }) => {
  const dayLabel = DAYS_OF_WEEK.find((d) => d.value === briefPrefs.dayOfWeek)?.long || "Monday";
  const timeLabel = HOUR_CHOICES.find((h) => h.value === briefPrefs.hour)?.label || "7 AM";

  return (
    <div data-testid="onboarding-step-7">
      <StepTitle
        eyebrow="Step 7 of 7"
        title="When would you like your weekly brief?"
        sub="One clean email a week — nothing else. Pick the day and time that fits your schedule. You can change this anytime."
      />

      <div className="mt-8">
        <div
          className="text-xs uppercase tracking-widest font-semibold mb-3"
          style={{ color: "var(--ta-secondary)" }}
        >
          Day of the week
        </div>
        <div className="grid grid-cols-4 sm:grid-cols-7 gap-2" data-testid="brief-day-picker">
          {DAYS_OF_WEEK.map((d) => {
            const active = briefPrefs.dayOfWeek === d.value;
            return (
              <button
                key={d.value}
                type="button"
                data-testid={`brief-day-${d.short.toLowerCase()}`}
                onClick={() => setBriefPrefs({ ...briefPrefs, dayOfWeek: d.value })}
                className="min-h-[56px] rounded-2xl border-2 text-sm font-semibold transition-transform hover:-translate-y-0.5"
                style={{
                  background: active ? "var(--ta-primary)" : "#fff",
                  color: active ? "#fff" : "var(--ta-primary)",
                  borderColor: active ? "var(--ta-primary)" : "var(--ta-border)",
                }}
              >
                {d.short}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-7">
        <div
          className="text-xs uppercase tracking-widest font-semibold mb-3"
          style={{ color: "var(--ta-secondary)" }}
        >
          Time
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="brief-hour-picker">
          {HOUR_CHOICES.map((h) => {
            const active = briefPrefs.hour === h.value;
            return (
              <button
                key={h.value}
                type="button"
                data-testid={`brief-hour-${h.value}`}
                onClick={() => setBriefPrefs({ ...briefPrefs, hour: h.value })}
                className="min-h-[56px] rounded-2xl border-2 text-sm font-semibold transition-transform hover:-translate-y-0.5"
                style={{
                  background: active ? "var(--ta-primary)" : "#fff",
                  color: active ? "#fff" : "var(--ta-primary)",
                  borderColor: active ? "var(--ta-primary)" : "var(--ta-border)",
                }}
              >
                {h.label}
              </button>
            );
          })}
        </div>
      </div>

      <div
        className="mt-8 p-4 rounded-2xl text-sm"
        data-testid="brief-time-preview"
        style={{ background: "var(--ta-surface-alt)", color: "var(--ta-primary)" }}
      >
        You'll get your weekly brief every <span className="font-semibold">{dayLabel}</span> at{" "}
        <span className="font-semibold">{timeLabel}</span> (your local time).
      </div>
    </div>
  );
};

const Completion = ({ onGo }) => (
  <WelcomeExperience onContinue={onGo} />
);

/* ---------- Shared ---------- */
const StepTitle = ({ eyebrow, title, sub }) => (
  <div>
    <div className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "var(--ta-secondary)" }}>
      {eyebrow}
    </div>
    <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.1]" style={{ color: "var(--ta-primary)" }}>
      {title}
    </h2>
    {sub && (
      <p className="mt-3 text-base sm:text-lg leading-relaxed" style={{ color: "var(--ta-text-secondary)" }}>
        {sub}
      </p>
    )}
  </div>
);
